import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import type moment from 'moment'
import clsx from 'clsx'
import Sheet from 'components/utils/Sheet'
import Divider from 'LuxKit/Divider'
import type { TripItem } from 'tripPlanner/types/tripItem'
import TravelDurationIndicator from './travelDuration'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import {
  generateGoogleMapsDirectionsUrl,
  getItemPlace,
} from 'tripPlanner/utils'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { getDestinationPlace } from './durationUtils'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import SelectItemToAdd from '../TripModal/TripItems/SelectItemToAdd'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import useDesktopWithoutTouch from 'tripPlanner/hooks/useDesktopWithoutTouch'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Tooltip from 'LuxKit/Tooltip'
import BodyText from 'LuxKit/Typography/BodyText'

const DurationLineWrapper = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  margin-top: ${rem(12)};
  padding: 0 ${rem(46)} 0 ${rem(2)};

  @container (min-width: 640px) {
    margin-top: ${rem(16)};
    padding: 0 ${rem(54)} 0 ${rem(2)};
  }
`

const ControlsGroup = styled(Group)`
  &.shouldHoverReveal {
    @container (min-width: 640px) {
      opacity: 0;
      pointer-events: none;
      
      ${DurationLineWrapper}:hover &,
      ${DurationLineWrapper}:focus-within & {
        opacity: 1;
        pointer-events: auto;
      }
    }
  }
`

const AddToTripButton = styled(AssistChip)`
  box-shadow: none;
  &.variant-outline {
    background-color: ${props => props.theme.palette.neutral.default.seven};

    &:not(:disabled) {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.default.five};
      }

      &:focus,
      &:active {
        background-color: ${props => props.theme.palette.neutral.default.six};
      }
    }
  }
`

interface Props {
  fromItem: TripItem
  toItem: TripItem
  allDayItems?: Array<TripItem>
  currentDate?: moment.Moment
  fromHotel?: boolean
  toHotel?: boolean
  isLoading?: boolean
}

function DurationLineContainer({ fromItem, toItem, allDayItems, currentDate, fromHotel, toHotel, isLoading }: Props) {
  const intl = useIntl()
  const { data: trip } = useTrip({ tripId: fromItem.tripId })
  const openModal = useOpenModal()
  const shouldHoverReveal = useDesktopWithoutTouch()

  const onAddToTrip = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('button', 'add_to_trip_between_activities'))
    if (!trip?.id) {
      showSnackbar(intl.formatMessage({
        id: 'c2X9GXyFMWOr69tpV_w2',
        defaultMessage: 'An error occurred, please try again later.',
        description: 'Error snackbar shown when adding an item between activities on the trip timeline fails',
      }), 'warning')
      return
    }

    const date = currentDate?.format('YYYY-MM-DD')

    if (!date) {
      openModal(SelectItemToAdd, { tripId: trip.id })
    } else {
      openModal(SelectItemToAdd, {
        tripId: trip.id,
        defaultDate: date,
        rankSpec: {
          type: 'itinerary',
          date: date,
          position: {
            type: 'after',
            itemId: fromItem.id,
          },
        },
      })
    }
  }, [intl, openModal, trip?.id, currentDate, fromItem.id])

  const mapsUrl = useMemo(() => {
    const destinationPlace = getDestinationPlace(fromItem, toItem, allDayItems, currentDate)
    if (destinationPlace?.type === 'GEO' && destinationPlace.point) {
      const destination = {
        lat: destinationPlace.point.lat,
        lng: destinationPlace.point.lng,
        name: destinationPlace.name,
        address: destinationPlace.address,
      }

      const originPlace = getItemPlace(fromItem, 'end')
      if (originPlace?.type === 'GEO' && originPlace.point) {
        return generateGoogleMapsDirectionsUrl(
          destination,
          {
            lat: originPlace.point.lat,
            lng: originPlace.point.lng,
            name: originPlace.name,
            address: originPlace.address,
          },
        )
      }

      // If no previous place found, just show destination
      return generateGoogleMapsDirectionsUrl(destination)
    }
    return undefined
  }, [fromItem, toItem, allDayItems, currentDate])

  const mapsButtonLabel = intl.formatMessage({
    id: 'exJB29qw0_t9hoeHdmqO',
    defaultMessage: 'Directions',
    description: 'Link label to open Google Maps directions between trip items',
  })
  let flightItems = false
  if (fromItem.type === 'FLIGHT' && toItem.type === 'FLIGHT') {
    flightItems = true
  }

  if (flightItems) {
    return (
      <Sheet padding="16 0" background="neutral-eight">
        <Divider kind="primary" />
      </Sheet>
    )
  }

  return (
    <DurationLineWrapper direction="horizontal" gap={12} verticalAlign="center" fullWidth>
      <TravelDurationIndicator fromItem={fromItem} toItem={toItem} allDayItems={allDayItems} currentDate={currentDate} fromHotel={fromHotel} toHotel={toHotel} />
      <ControlsGroup
        direction="horizontal-reverse"
        gap={12}
        verticalAlign="center"
        horizontalAlign="space-between"
        grow
        className={clsx({ shouldHoverReveal })}
      >
        <Tooltip description={
          <BodyText
            variant="small"
            weight="normal"
            colour="neutral-eight"
            align="center"
          >
            {intl.formatMessage({
              id: 'D1N_4brWj4KPo8A-q7H1',
              defaultMessage: 'Add to trip',
              description: 'Tooltip for button to add an item between activities on the trip timeline',
            })}
          </BodyText>
        }
        >
          <AddToTripButton
            variant="outline"
            onClick={onAddToTrip}
            size="small"
            startIcon={<LinePlusIcon size="XS" />}
          />
        </Tooltip>
        {!isLoading && mapsUrl && (
          <TextLink
            href={mapsUrl}
            size="caption"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Group direction="horizontal" gap={8} verticalAlign="center">
              {mapsButtonLabel}
              <LineExternalLinkAltIcon size="XS" />
            </Group>
          </TextLink>
        )}
      </ControlsGroup>
    </DurationLineWrapper>
  )
}

export default DurationLineContainer
